import { tr } from '@/lib/i18n-core';
import { minorFromInput } from '@/components/MoneyInput';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { useWorkingBranchId } from '@/lib/branch';
import { Banknote, CheckCircle2, Clock3, HandCoins, Lock, Wallet } from 'lucide-react';
import { cn, dateOnly, dateTime, display, humanize, money } from '@/lib/utils';
import { visibleInterval } from '@/lib/polling';
import { PageHeader, StatTile } from '@/components/ui/layout';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { countedDenominations, countedMinor, denominationKey, denominationsFor, type Denomination } from './cash-denominations';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, SkeletonText } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';

interface ShiftReport {
  id: string;
  counterName: string;
  cashier: { id: string; name: string };
  openedAt: string;
  closedAt: string | null;
  openingFloatMinor: number;
  blindCount: boolean;
  expectedCashMinor: number | null;
  cashDepositsMinor: number | null;
  cashRefundedMinor: number | null;
  refundCount: number;
  refundedMinor: number;
  depositCount: number;
  depositsMinor: number;
  countedCashMinor: number | null;
  varianceMinor: number;
  invoiceCount: number;
  paymentCount: number;
  byMode: { mode: string; count: number; amountMinor: number | null }[];
  totalMinor: number | null;
}

/**
 * Cashier shift: float in, X report while open, denomination count at close
 * (SRS 9.2). The variance is shown before the shift is committed, so a
 * miscount is caught at the counter rather than in the morning.
 */
interface RecentShift {
  id: string;
  counterName: string;
  openedAt: string;
  closedAt: string | null;
  openingFloatMinor: number;
  expectedCashMinor: number;
  countedCashMinor: number | null;
  varianceMinor: number;
}

export function ShiftPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const workingBranchId = useWorkingBranchId();
  const [float, setFloat] = useState('0');
  const [counter, setCounter] = useState('Main Counter');
  const [floatCounts, setFloatCounts] = useState<Record<string, string>>({});
  const [floatLoose, setFloatLoose] = useState('');
  const [counts, setCounts] = useState<Record<string, string>>({});
  const [closeLoose, setCloseLoose] = useState('');
  const [confirmingClose, setConfirmingClose] = useState(false);
  const denominations = denominationsFor(display.currency);

  const shift = useQuery({
    queryKey: ['shift', 'current'],
    queryFn: () => get<ShiftReport | null>('/shifts/current'),
    refetchInterval: visibleInterval(60_000),
  });

  const recent = useQuery({
    queryKey: ['shift', 'recent'],
    queryFn: () => get<RecentShift[]>('/shifts/recent?limit=5'),
    enabled: shift.data === null,
  });

  const floatFromNotes = countedMinor(denominations, floatCounts, minorFromInput(floatLoose));
  const openingFloat = floatFromNotes > 0 ? floatFromNotes : minorFromInput(float);

  const openShift = useMutation({
    mutationFn: () =>
      post('/shifts/open', { counterName: counter.trim() || 'Main Counter', openingFloatMinor: openingFloat, branchId: workingBranchId || undefined }),
    onSuccess: () => {
      toast.success(tr('billing', 'shift_opened'));
      void queryClient.invalidateQueries({ queryKey: ['shift'] });
    },
    onError: (error) => toast.error(tr('billing', 'could_not_open_the_shift'), describeError(error)),
  });

  const looseAtClose = minorFromInput(closeLoose);
  const looseValid = Number.isFinite(looseAtClose) && looseAtClose >= 0;
  const counted = countedMinor(denominations, counts, looseValid ? looseAtClose : 0);
  const countedCash = counted - (looseValid ? looseAtClose : 0);

  const closeShift = useMutation({
    mutationFn: (shiftId: string) =>
      post<{ expectedCashMinor: number; varianceMinor: number }>(`/shifts/${shiftId}/close`, {
        countedCashMinor: counted,
        denominations: countedDenominations(denominations, counts),
        looseCashMinor: looseValid && looseAtClose > 0 ? looseAtClose : undefined,
      }),
    onSuccess: (closed) => {
      toast.success(
        tr('billing', 'shift_closed'),
        `Expected ${money(closed.expectedCashMinor)}, counted ${money(counted)}${
          closed.varianceMinor === 0 ? ': the drawer balances.' : `: ${closed.varianceMinor > 0 ? 'over' : 'short'} by ${money(Math.abs(closed.varianceMinor))}.`
        }`,
      );
      setCounts({});
      setCloseLoose('');
      setConfirmingClose(false);
      void queryClient.invalidateQueries({ queryKey: ['shift'] });
    },
    onError: (error) => toast.error(tr('billing', 'could_not_close_the_shift'), describeError(error)),
  });

  if (shift.isLoading) {
    return (
      <SkeletonText lines={3} />
    );
  }

  const open = shift.data && shift.data.id;

  if (!open) {
    const branchName = (user?.branches ?? []).find((branch) => branch.id === workingBranchId)?.name;
    const steps = [
      { icon: Wallet, title: tr('billing', 'step_open'), hint: tr('billing', 'step_open_hint') },
      { icon: HandCoins, title: tr('billing', 'step_take'), hint: tr('billing', 'step_take_hint') },
      { icon: Lock, title: tr('billing', 'step_close'), hint: tr('billing', 'step_close_hint') },
    ];
    return (
      <>
        <PageHeader title={tr('billing', 'cashier_shift')} description={tr('billing', 'open_a_shift_before_taking_payments')} />
        <div className="mx-auto max-w-5xl">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)]">
            <Card className="overflow-hidden">
              <div className="border-b bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 py-6 text-center">
                <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30">
                  <Banknote className="h-7 w-7" aria-hidden />
                </span>
                <h2 className="mt-3 text-xl font-semibold tracking-tight">{tr('billing', 'shift_hero_title')}</h2>
                <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{tr('billing', 'shift_hero_body')}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Badge variant="outline">{user?.displayName}</Badge>
                  {branchName && <Badge variant="outline">{branchName}</Badge>}
                  <Badge variant="outline">
                    <Clock3 className="h-3 w-3" aria-hidden /> {dateOnly(new Date().toISOString())}
                  </Badge>
                </div>
              </div>
              <CardContent className="space-y-5 px-6 py-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={tr('billing', 'counter')}>
                    <Input value={counter} onChange={(e) => setCounter(e.target.value)} />
                  </Field>
                  <Field label={tr('billing', 'opening_float')} hint={tr('billing', 'cash_already_in_the_drawer')}>
                    <Input
                      type="text"
                      inputMode="decimal"
                      dir="ltr"
                      value={floatFromNotes > 0 ? (floatFromNotes / 100).toFixed(0) : float}
                      disabled={floatFromNotes > 0}
                      onChange={(e) => setFloat(e.target.value)}
                      className="tabular"
                    />
                  </Field>
                </div>
                <div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{tr('billing', 'count_the_float')}</p>
                  <DenominationGrid denominations={denominations} counts={floatCounts} onChange={setFloatCounts} compact />
                  <Field label={tr('billing', 'loose_cash_other_amount')} hint={tr('billing', 'loose_cash_hint')} className="mt-3">
                    <Input type="text" inputMode="decimal" dir="ltr" value={floatLoose} onChange={(e) => setFloatLoose(e.target.value)} className="tabular" />
                  </Field>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-muted/50 px-4 py-3">
                  <div>
                    <p className="text-xs text-muted-foreground">{tr('billing', 'float_total')}</p>
                    <p className="text-2xl font-semibold tabular">{money(openingFloat)}</p>
                  </div>
                  <Button size="lg" className="min-w-[12rem]" onClick={() => openShift.mutate()} loading={openShift.isPending}>
                    <Wallet className="h-4 w-4" aria-hidden /> {tr('billing', 'open_shift')}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="space-y-5">
              <Card>
                <CardContent className="space-y-3 p-5">
                  {steps.map((step, index) => (
                    <div key={step.title} className="flex items-start gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                        <step.icon className="h-4 w-4" aria-hidden />
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          <span className="me-1.5 text-muted-foreground">{index + 1}.</span>
                          {step.title}
                        </p>
                        <p className="text-xs text-muted-foreground">{step.hint}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">{tr('billing', 'your_last_shifts')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {recent.isLoading && <SkeletonText lines={3} />}
                  {recent.data && recent.data.length === 0 && <p className="text-sm text-muted-foreground">{tr('billing', 'no_shifts_yet')}</p>}
                  {recent.data?.map((row) => {
                    const tone = row.varianceMinor === 0 ? 'success' : Math.abs(row.varianceMinor) < 10_000 ? 'warning' : 'destructive';
                    const label =
                      row.varianceMinor === 0
                        ? tr('billing', 'balanced')
                        : row.varianceMinor > 0
                          ? tr('billing', 'over_by_x', { amount: money(row.varianceMinor) })
                          : tr('billing', 'short_by_x', { amount: money(Math.abs(row.varianceMinor)) });
                    return (
                      <div key={row.id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm">
                        <div className="min-w-0">
                          <p className="truncate font-medium">{row.counterName}</p>
                          <p className="text-xs text-muted-foreground">{row.closedAt ? dateTime(row.closedAt) : dateTime(row.openedAt)}</p>
                        </div>
                        <div className="text-end">
                          <p className="tabular">{money(row.expectedCashMinor)}</p>
                          <Badge variant={tone} className={cn('mt-0.5')}>
                            {row.varianceMinor === 0 && <CheckCircle2 className="h-3 w-3" aria-hidden />}
                            {label}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </>
    );
  }

  const report = shift.data as ShiftReport;
  const expected = report.expectedCashMinor;
  const variance = expected === null ? 0 : counted - expected;

  return (
    <>
      <PageHeader
        title={tr('billing', 'cashier_shift')}
        description={tr('billing', 'x_opened_x_by_x', { counterName: report.counterName, openedAt: dateTime(report.openedAt), name: report.cashier.name })}
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={tr('billing', 'opening_float')} value={money(report.openingFloatMinor)} />
        <StatTile
          label={tr('billing', 'collected')}
          value={report.totalMinor === null ? 'Hidden until you close' : money(report.totalMinor)}
          sub={tr('billing', 'x_payment_s', { paymentCount: report.paymentCount })}
        />
        <StatTile label={tr('billing', 'invoices')} value={report.invoiceCount} />
        <StatTile
          label={tr('billing', 'expected_in_drawer')}
          value={expected === null ? 'Count first, then it is shown' : money(expected)}
          tone="success"
        />
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-2">
        <StatTile
          label={tr('billing', 'advances_taken_and_refunded')}
          value={money(report.depositsMinor)}
          sub={`${report.depositCount} entr${report.depositCount === 1 ? 'y' : 'ies'}${report.cashDepositsMinor === null ? '' : ` · cash ${money(report.cashDepositsMinor)}`}`}
        />
        <StatTile
          label={tr('billing', 'refunds_paid_out')}
          value={money(report.refundedMinor)}
          sub={`${report.refundCount} refund(s)${report.cashRefundedMinor === null ? '' : ` · cash ${money(report.cashRefundedMinor)}`}`}
          tone={report.refundCount > 0 ? 'warning' : 'default'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('billing', 'taken_so_far_by_payment_mode')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 text-sm">
            {report.byMode.length === 0 && <p className="text-muted-foreground">{tr('billing', 'no_payments_yet_this_shift')}</p>}
            {report.byMode.map((row) => (
              <div key={row.mode} className="flex justify-between border-b py-1 last:border-0">
                <span>
                  {humanize(row.mode)} <span className="text-muted-foreground">({row.count})</span>
                </span>
                <span className="tabular">{row.amountMinor === null ? '—' : money(row.amountMinor)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">{tr('billing', 'close_shift_count_the_drawer')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <DenominationGrid denominations={denominations} counts={counts} onChange={setCounts} />
            <Field label={tr('billing', 'loose_cash_other_amount')} hint={tr('billing', 'loose_cash_hint')} error={looseValid ? undefined : tr('billing', 'enter_an_amount_of_zero_or_more')}>
              <Input type="text" inputMode="decimal" dir="ltr" value={closeLoose} onChange={(e) => setCloseLoose(e.target.value)} className="tabular" />
            </Field>

            <div className="flex justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
              <span>{tr('billing', 'counted')}</span>
              <span className="tabular font-semibold">{money(counted)}</span>
            </div>

            {expected === null && (
              <Alert>{tr('billing', 'count_the_drawer_without_looking_at_the_system')}</Alert>
            )}

            {counted > 0 && expected !== null && (
              <Alert variant={variance === 0 ? 'success' : Math.abs(variance) < 10_000 ? 'warning' : 'destructive'}>
                {variance === 0
                  ? tr('billing', 'the_drawer_balances_exactly')
                  : `${variance > 0 ? 'Over' : 'Short'} by ${money(Math.abs(variance))}`}
              </Alert>
            )}

            <Button
              className="w-full"
              variant={expected === null || variance === 0 ? 'default' : 'destructive'}
              onClick={() => setConfirmingClose(true)}
              loading={closeShift.isPending}
              disabled={!looseValid}
            >
              {tr('billing', 'close_shift')}
            </Button>
          </CardContent>
        </Card>
      </div>

      {confirmingClose && (
        <ConfirmDialog
          title={tr('billing', 'close_this_shift')}
          confirmLabel={tr('billing', 'close_shift')}
          destructive={expected !== null && variance !== 0}
          onConfirm={() => closeShift.mutateAsync(report.id)}
          onClose={() => setConfirmingClose(false)}
        >
          <dl className="space-y-1.5 text-sm">
            <SummaryRow label={tr('billing', 'notes_and_coins')} value={money(countedCash)} />
            <SummaryRow label={tr('billing', 'loose_cash_other_amount')} value={money(looseValid ? looseAtClose : 0)} />
            <SummaryRow label={tr('billing', 'counted')} value={money(counted)} strong />
            {expected !== null && <SummaryRow label={tr('billing', 'expected_in_drawer')} value={money(expected)} />}
            {expected !== null && (
              <SummaryRow
                label={tr('billing', 'variance')}
                value={variance === 0 ? tr('billing', 'balanced') : variance > 0 ? tr('billing', 'over_by_x', { amount: money(variance) }) : tr('billing', 'short_by_x', { amount: money(Math.abs(variance)) })}
              />
            )}
          </dl>
          {counted === 0 && <Alert variant="warning" className="mt-3">{tr('billing', 'closing_with_an_empty_drawer')}</Alert>}
        </ConfirmDialog>
      )}
    </>
  );
}

function SummaryRow({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('tabular', strong && 'font-semibold')}>{value}</dd>
    </div>
  );
}

function DenominationGrid({
  denominations,
  counts,
  onChange,
  compact,
}: {
  denominations: Denomination[];
  counts: Record<string, string>;
  onChange: (update: (current: Record<string, string>) => Record<string, string>) => void;
  compact?: boolean;
}) {
  if (denominations.length === 0) return <p className="text-sm text-muted-foreground">{tr('billing', 'no_note_list_for_this_currency')}</p>;
  return (
    <div className={cn('grid grid-cols-2 gap-2', compact && 'md:grid-cols-3 lg:grid-cols-2')}>
      {denominations.map((denomination) => {
        const key = denominationKey(denomination);
        const label = denomination.kind === 'coin' ? tr('billing', 'coin_x', { value: denomination.face }) : String(denomination.face);
        return (
          <label key={key} className="flex items-center gap-2 rounded-lg border bg-muted/30 px-2.5 py-1.5">
            <span className="w-16 shrink-0 text-end text-sm font-medium tabular">{label}</span>
            <span className="text-muted-foreground">×</span>
            <Input
              type="number"
              min={0}
              step={1}
              value={counts[key] ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                onChange((current) => ({ ...current, [key]: value }));
              }}
              className="h-8 tabular"
              aria-label={label}
            />
          </label>
        );
      })}
    </div>
  );
}
